import { Box, Link as MUILink, Typography } from '@mui/material'
import { AccessconflictV1AccessConflictServiceowner } from 'c1gen/roles'
import { InsightItem } from 'components/core/insights'
import { C1Link } from 'components/core/links/C1Link'
import { useHasRole } from 'components/no-ui/capability'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { AccessConflictData, AccessConflictOutcome, Outcome } from 'pbts/c1/api/analysis/v1/analysis'
import { InsightType } from 'pbts/c1/api/task/v1/insights'
import { FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'

type AccessConflictLabelProps = {
    task: TaskValue;
}

export const AccessConflictLabel: FC<AccessConflictLabelProps> = ({ task }) => {
    return (
        <Typography>
            <FormattedMessage
                id="components_insights_access_conflict"
                defaultMessage="This access {taskType, select, grant {will create} certify {triggered} other {will create}} access conflict alerts."
                values={{
                    taskType: task.type?.taskType.oneofKind,
                }}
            />
        </Typography>
    )
}

type AccessConflictProps = {
    outcome: Outcome | undefined;
    insightType: InsightType
    task: TaskValue;
}

export const canRenderAccessConflict = (outcome: Outcome | undefined): boolean => {
    if (!outcome?.outcome) return false
    const accessConflictOutcome = outcome.outcome as unknown as AccessConflictOutcome
    return accessConflictOutcome.monitors.length > 0
}

export const AccessConflict: FC<AccessConflictProps> = ({ outcome, insightType, task }) => {
    const [showingAllMonitors, setShowingAllMonitors] = useState(false)

    if (!canRenderAccessConflict(outcome)) return null

    // Safe to assert: canRenderAccessConflict above guarantees outcome.outcome exists
    const accessConflictOutcome = (outcome as Outcome).outcome as unknown as AccessConflictOutcome

    const monitorsToShow = accessConflictOutcome.monitors.slice(0, 4)
    const additionalMonitorsCount = accessConflictOutcome.monitors.length - 4

    return (
        <>
            {(showingAllMonitors ? accessConflictOutcome.monitors : monitorsToShow).map((monitorInsight) => (
                <InsightItem key={monitorInsight.monitorId} insightType={insightType}>
                    <Typography>
                        <AccessConflictInsightFormattedMessage
                            task={task}
                            monitorInsight={monitorInsight}
                        />
                    </Typography>
                </InsightItem>
            ))}
            {(additionalMonitorsCount > 0 && !showingAllMonitors) && (
                <Box sx={{
                    paddingTop: 0,
                    paddingBottom: 2,
                    paddingLeft: 5,
                    display: 'flex',
                    alignItems: 'center',
                }}
                >
                    <Typography variant="caption">
                        <MUILink onClick={(): void => setShowingAllMonitors(!showingAllMonitors)}>
                            <FormattedMessage
                                id="components_insights_access_conflict_additional"
                                defaultMessage="+ {count, plural, one {# other monitor} other {# other monitors}}"
                                values={{ count: additionalMonitorsCount }}
                            />
                        </MUILink>
                    </Typography>
                </Box>
            )}
        </>
    )
}

type AccessConflictInsightFormattedMessageProps = {
    task: TaskValue;
    monitorInsight: AccessConflictData;
}

export const AccessConflictInsightFormattedMessage: FC<AccessConflictInsightFormattedMessageProps> = ({ task, monitorInsight }) => {
    const isAccessConflictOwner = useHasRole(AccessconflictV1AccessConflictServiceowner)

    return <FormattedMessage
        id="components_insights_access_conflict"
        defaultMessage="This access {taskType, select, grant {will create} certify {triggered} other {will create}} alerts in the <link>{monitor_name}</link> conflict monitor"
        values={{
            monitor_name: monitorInsight.monitorDisplayName,
            taskType: task.type?.taskType.oneofKind,
            link: (chunks) =>
                (isAccessConflictOwner
                    ? <C1Link target="_blank" href={routes.conflictMonitors.detail(monitorInsight.monitorId)}>{chunks}</C1Link>
                    : <strong>{chunks}</strong>),
        }}
    />
}
